import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { visibleChipCount } from "./visibleChipCount";

const CHIP_GAP = 6;

const StaffingCatalogSelect = ({
	label,
	items,
	selected,
	open,
	query,
	onToggleOpen,
	onQuery,
	onToggleItem,
	onClear,
	chipOf,
	searchPlaceholder,
	catalogEmpty,
	emptyLabel,
	noMatchesLabel,
	selectedLabel,
	clearLabel,
	catalogId,
	invalid,
}) => {
	const fieldRef = useRef(null);
	const measureRef = useRef(null);
	const overflowRef = useRef(null);
	const [visibleCount, setVisibleCount] = useState(selected.length);
	const [menuBox, setMenuBox] = useState(null);

	useLayoutEffect(() => {
		const measure = () => {
			const field = fieldRef.current;
			const measureBox = measureRef.current;
			if (!field || !measureBox) return;
			const chips = measureBox.querySelectorAll("[data-sl-measure-chip]");
			const widths = Array.from(chips).map((chip) => chip.getBoundingClientRect().width);
			const overflowWidth = overflowRef.current
				? overflowRef.current.getBoundingClientRect().width
				: 36;
			const style = window.getComputedStyle(field);
			const padding =
				parseFloat(style.paddingLeft || "0") + parseFloat(style.paddingRight || "0");
			const available = field.clientWidth - padding - 28;
			setVisibleCount(visibleChipCount(widths, available, CHIP_GAP, overflowWidth));
		};

		measure();
		if (typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(measure);
		if (fieldRef.current) observer.observe(fieldRef.current);
		return () => observer.disconnect();
	}, [selected, open]);

	useLayoutEffect(() => {
		if (!open || !fieldRef.current) {
			setMenuBox(null);
			return undefined;
		}
		const place = () => {
			const field = fieldRef.current;
			if (!field) return;
			const rect = field.getBoundingClientRect();
			const next = {
				top: rect.bottom + 6,
				left: rect.left,
				width: rect.width,
			};
			setMenuBox((prev) =>
				prev &&
				prev.top === next.top &&
				prev.left === next.left &&
				prev.width === next.width
					? prev
					: next
			);
		};
		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [open]);

	useEffect(() => {
		if (!open || !menuBox) return undefined;
		const selector = catalogId
			? `[data-sl-catalog="${catalogId}"].sl-catalog-menu input[type="search"]`
			: ".sl-catalog-menu input[type='search']";
		const input = document.querySelector(selector);
		if (input && document.activeElement !== input) input.focus();
		return undefined;
	}, [open, menuBox, catalogId]);

	const shown = selected.slice(0, visibleCount);
	const hidden = Math.max(0, selected.length - shown.length);

	return (
		<div
			className={`sl-catalog${open ? " is-open" : ""}${invalid ? " is-invalid" : ""}`}
			data-sl-catalog={catalogId || "true"}
		>
			{label ? <span className="sl-catalog-label">{label}</span> : null}
			<div
				className="sl-catalog-field"
				ref={fieldRef}
				role="button"
				tabIndex={0}
				aria-expanded={open}
				aria-invalid={invalid || undefined}
				onClick={onToggleOpen}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						onToggleOpen();
					}
				}}
			>
				<span className="sl-catalog-chips">
					{shown.map((item) => {
						const chip = chipOf(item);
						return (
							<span key={item.id} className="sl-chip" style={chip.style}>
								<span className="sl-chip-name">{chip.label}</span>
								<button
									type="button"
									className="sl-chip-remove"
									aria-label={chip.removeLabel}
									onClick={(event) => {
										event.stopPropagation();
										onToggleItem(item);
									}}
								>
									<FiX aria-hidden="true" />
								</button>
							</span>
						);
					})}
					{hidden > 0 && <span className="sl-chip sl-chip-more">+{hidden}</span>}
				</span>
				<FiChevronDown className="sl-catalog-caret" aria-hidden="true" />
			</div>

			<div className="sl-catalog-measure" ref={measureRef} aria-hidden="true">
				{selected.map((item) => {
					const chip = chipOf(item);
					return (
						<span key={item.id} className="sl-chip" data-sl-measure-chip="true" style={chip.style}>
							<span className="sl-chip-name">{chip.label}</span>
							<FiX aria-hidden="true" />
						</span>
					);
				})}
				<span className="sl-chip sl-chip-more" ref={overflowRef}>
					+99
				</span>
			</div>

			{open &&
				menuBox &&
				createPortal(
				<div
					className="sl-catalog-menu"
					data-sl-catalog={catalogId || "true"}
					style={{
						top: menuBox.top,
						left: menuBox.left,
						width: menuBox.width,
					}}
				>
					<div className="sl-catalog-search">
						<input
							type="search"
							value={query}
							placeholder={searchPlaceholder}
							aria-label={searchPlaceholder}
							onChange={(event) => onQuery(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") event.preventDefault();
							}}
						/>
						<FiSearch aria-hidden="true" />
					</div>
					<ul className="sl-catalog-list">
						{catalogEmpty && <li className="sl-catalog-empty">{emptyLabel}</li>}
						{!catalogEmpty && items.length === 0 && (
							<li className="sl-catalog-empty">{noMatchesLabel}</li>
						)}
						{items.map((item) => {
							const on = selected.some((entry) => entry.id === item.id);
							const chip = chipOf(item);
							return (
								<li key={item.id}>
									<label>
										<input
											type="checkbox"
											checked={on}
											onChange={() => onToggleItem(item)}
										/>
										<span className="sl-chip" style={chip.style}>
											{chip.label}
										</span>
									</label>
								</li>
							);
						})}
					</ul>
					<div className="sl-catalog-foot">
						<span>{selectedLabel}</span>
						<button type="button" onClick={onClear} disabled={!selected.length}>
							{clearLabel}
						</button>
					</div>
				</div>,
					document.body
				)}
		</div>
	);
};

export default StaffingCatalogSelect;
